/**
 * Pure client-side approve gates for designer invoice review UI.
 * Keep Firebase-free so Jest can unit-test without TextEncoder/storage.
 */

export type DesignerInvoicePhase = 'estimating' | 'precon' | 'under_contract' | 'overhead';
export type DesignerInvoiceBucket =
  | 'external_design'
  | 'external_engineering'
  | 'training_onboarding'
  | 'other';
export type DesignerInvoiceOutcome = 'open' | 'won' | 'lost';

export interface DesignerInvoiceLine {
  id: string;
  description?: string | null;
  amount: number;
  hours?: number | null;
  rate?: number | null;
  dealId?: string | null;
  projectId?: string | null;
  dealLabel?: string | null;
  projectLabel?: string | null;
  phase: DesignerInvoicePhase;
  bucket?: DesignerInvoiceBucket;
  outcome?: DesignerInvoiceOutcome | null;
}

export interface ApproveGateIssue {
  code: string;
  message: string;
  lineId?: string;
}

const AMOUNT_TOLERANCE = 0.01;

export type HubProjectLite = {
  id?: string;
  dealId?: string | null;
  name?: string | null;
  phase?: string | null;
  status?: string | null;
};

function lineLabel(id: string): string {
  const match = String(id).match(/^line_(\d+)/i);
  return match ? `Line ${match[1]}` : `Line ${id}`;
}

export function invoicePhaseToHubPhase(phase: DesignerInvoicePhase): string | null {
  if (phase === 'precon') return 'preconstruction';
  if (phase === 'under_contract') return 'contracted';
  if (phase === 'estimating') return 'estimating';
  return null;
}

/** Pick the Hub project for a mapped job, or fall back to the deal id so approve can proceed. */
export function resolveDesignerInvoiceHubProject(
  line: Pick<DesignerInvoiceLine, 'dealId' | 'phase' | 'projectId' | 'projectLabel'>,
  projects: HubProjectLite[],
): { projectId: string | null; projectLabel: string | null } {
  const dealId = line.dealId?.trim() || null;
  if (!dealId || line.phase === 'overhead') {
    return { projectId: null, projectLabel: null };
  }
  const linked = projects.filter((project) => project.id && project.dealId === dealId);
  if (line.projectId && linked.some((project) => project.id === line.projectId)) {
    const keep = linked.find((project) => project.id === line.projectId);
    return {
      projectId: line.projectId,
      projectLabel: keep?.name || line.projectLabel || line.projectId,
    };
  }
  const wanted = invoicePhaseToHubPhase(line.phase);
  const phaseHits = wanted
    ? linked.filter((project) => (project.phase || 'contracted') === wanted)
    : [];
  const pick = phaseHits[0] || linked[0] || null;
  if (pick?.id) {
    return { projectId: pick.id, projectLabel: pick.name || pick.id };
  }
  return { projectId: dealId, projectLabel: line.projectLabel || null };
}

export function withResolvedHubProjects<T extends DesignerInvoiceLine>(
  lines: T[],
  projects: HubProjectLite[],
): T[] {
  let changed = false;
  const next = lines.map((line) => {
    if (line.phase === 'overhead' || !line.dealId) return line;
    const resolved = resolveDesignerInvoiceHubProject(line, projects);
    if (resolved.projectId === (line.projectId || null) && resolved.projectLabel === (line.projectLabel || null)) {
      return line;
    }
    changed = true;
    return { ...line, ...resolved };
  });
  return changed ? next : lines;
}

/** Client-side mirror of server approve gates (disable Approve until ready). */
export function evaluateClientApproveGates(input: {
  invoiceTotal: number;
  lines: DesignerInvoiceLine[];
}): { ok: boolean; issues: ApproveGateIssue[]; lineSum: number } {
  const issues: ApproveGateIssue[] = [];
  let lineSum = 0;
  if (!input.lines.length) {
    issues.push({ code: 'missing_lines', message: 'Add at least one allocation line' });
  }
  for (const line of input.lines) {
    const amount = Math.round(Number(line.amount) * 100) / 100;
    lineSum = Math.round((lineSum + amount) * 100) / 100;
    if (line.phase !== 'overhead') {
      if (!line.dealId?.trim()) {
        issues.push({
          code: 'non_oh_missing_deal',
          message: `${lineLabel(line.id)}: pick a job`,
          lineId: line.id,
        });
      }
      if (!line.projectId?.trim()) {
        issues.push({
          code: 'non_oh_missing_project',
          message: `${lineLabel(line.id)}: pick a job (Hub project is filled from that job)`,
          lineId: line.id,
        });
      }
    }
  }
  if (
    input.lines.length > 0 &&
    Math.abs(Math.round(lineSum * 100) - Math.round(input.invoiceTotal * 100)) >
      Math.round(AMOUNT_TOLERANCE * 100)
  ) {
    issues.push({
      code: 'sum_mismatch',
      message: `Line sum $${lineSum.toFixed(2)} must equal invoice $${input.invoiceTotal.toFixed(2)} ± $0.01`,
    });
  }
  return { ok: issues.length === 0, issues, lineSum };
}

export function newEmptyLine(index: number): DesignerInvoiceLine {
  return {
    id: `line_${index + 1}_${Date.now().toString(36)}`,
    description: '',
    amount: 0,
    hours: null,
    rate: null,
    dealId: null,
    projectId: null,
    dealLabel: null,
    projectLabel: null,
    phase: 'estimating',
    bucket: 'external_design',
    outcome: null,
  };
}

export function formatInvoiceMoney(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
